/**
 * The 220 px sidebar. Prototype: docs/HRMS.jsx line 3722-3798, minus one thing.
 *
 * **There is no "Viewing as" switcher here, and there must never be one.** The
 * prototype's persona control (line 3731-3741) is a review device; the PRD and both
 * behaviour docs exclude it from the product, and IMPLEMENTATION_DECISIONS D11 records
 * the decision. The nav set below is chosen by `me.workspace` and `me.capabilities` —
 * two values that only the server writes — so there is no code path, flag or prop by
 * which a signed-in user can assume another role. Everything else on this rail is
 * copied from the prototype.
 */
import { COMPANY, type Capabilities } from "@hrms/shared";
import {
  BellRing,
  BookOpen,
  CalendarDays,
  ClipboardCheck,
  Clock3,
  FolderOpen,
  Home,
  LayoutDashboard,
  LogOut,
  Moon,
  Network,
  PieChart,
  Receipt,
  Settings as SettingsIcon,
  Sun,
  User,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Avatar } from "@/components/ui";
import { useMe, useSession } from "@/lib/session";
import { useTheme, type Theme } from "@/lib/theme";

/* ---------------------------------- nav ---------------------------------- */

export type Workspace = "admin" | "manager" | "employee";

export interface NavLeaf {
  key: string;
  to: string;
  icon: LucideIcon;
}

export interface NavGroup {
  group: string;
  items: readonly NavLeaf[];
}

/** Prototype `ME_ITEMS` (line 3539-3547) — identical order, identical icons. */
export const ME_ITEMS: readonly NavLeaf[] = [
  { key: "Home", to: "/home", icon: Home },
  { key: "Onboarding", to: "/onboarding", icon: BookOpen },
  { key: "My Profile", to: "/me/profile", icon: User },
  { key: "My Leave", to: "/me/leave", icon: CalendarDays },
  { key: "My Attendance", to: "/me/attendance", icon: Clock3 },
  { key: "My Reviews", to: "/me/reviews", icon: ClipboardCheck },
  { key: "My Reimbursements", to: "/me/reimbursements", icon: Receipt },
];

/** Prototype `NAV` (line 3549-3581). Group order and item order are part of the design. */
export const NAV: Record<Workspace, readonly NavGroup[]> = {
  admin: [
    {
      group: "Organisation",
      items: [
        { key: "Dashboard", to: "/admin/dashboard", icon: LayoutDashboard },
        { key: "Teammates", to: "/admin/teammates", icon: Users },
        { key: "Org Chart", to: "/org-chart", icon: Network },
        { key: "Leave", to: "/admin/leave", icon: CalendarDays },
        { key: "Attendance", to: "/admin/attendance", icon: Clock3 },
        { key: "Reimbursements", to: "/admin/reimbursements", icon: Receipt },
        { key: "Reviews", to: "/admin/reviews", icon: ClipboardCheck },
        { key: "Reports", to: "/admin/reports", icon: PieChart },
        { key: "Resources", to: "/resources", icon: FolderOpen },
      ],
    },
    { group: "Me", items: ME_ITEMS },
  ],
  manager: [
    {
      group: "My team",
      items: [
        { key: "My Team", to: "/team", icon: Users },
        { key: "Team Reimbursements", to: "/team/reimbursements", icon: Receipt },
        { key: "Org Chart", to: "/org-chart", icon: Network },
      ],
    },
    { group: "Me", items: ME_ITEMS },
    { group: "Company", items: [{ key: "Resources", to: "/resources", icon: FolderOpen }] },
  ],
  employee: [
    { group: "Me", items: ME_ITEMS },
    {
      group: "Company",
      items: [
        { key: "Org Chart", to: "/org-chart", icon: Network },
        { key: "Resources", to: "/resources", icon: FolderOpen },
      ],
    },
  ],
};

/** Where "/" sends each workspace — the first nav item of the first group. */
export const LANDING_ROUTE: Record<Workspace, string> = {
  admin: "/admin/dashboard",
  manager: "/team",
  employee: "/home",
};

/** What "Back to …" is called in each workspace. Used by the Forbidden and 404 screens. */
export const LANDING_LABEL: Record<Workspace, string> = {
  admin: "Dashboard",
  manager: "My Team",
  employee: "Home",
};

/* --------------------------- capability-granted nav --------------------------- */

/**
 * Screens a *capability* opens, for people whose workspace does not.
 *
 * `me.workspace` decides which rail you get. It was also deciding what you may open,
 * and those are not the same question. Finance is the case that proves it:
 * `capabilitiesFor` in `apps/server/src/services/authz.ts` grants the access role
 * `canViewReports` and `canPayClaims`, and `ROLE_MATRIX` advertises "View & export"
 * reports and "Process & pay" reimbursements against the Finance row of the Settings
 * table HR reads before assigning the role. But `workspaceFor` derives the workspace
 * from the *effective* role, and a Finance person with no reports is a `member` — so
 * they landed in the employee workspace, `/admin/reports` and `/admin/reimbursements`
 * both answered "Not available to you", and the rail offered neither. The server was
 * saying yes to a door the client would not draw.
 *
 * Each entry names the capability the server itself checks on that endpoint, so the
 * two cannot drift:
 *
 *   · `canViewReports`  — `routes/reports.ts` requires it on all three report routes;
 *   · `canPayClaims`    — `services/claims.ts` requires it to release a payment, and
 *     it is `isAdmin || isFinance`, which is exactly the set that should see the
 *     company-wide claims queue. Deliberately *not* `canAdministerClaims`, which also
 *     covers managers: a manager's queue is `/team/reimbursements`, scoped to their
 *     chain, and is already on their rail.
 *
 * Nothing else belongs here. Finance's other matrix cells are "Self only" (Teammates),
 * "Own leave", "Own reviews" and Settings "None" — all of which the employee rail
 * already covers or correctly withholds.
 */
export interface CapabilityLeaf extends NavLeaf {
  capability: keyof Capabilities;
}

export const CAPABILITY_ITEMS: readonly CapabilityLeaf[] = [
  { key: "Reimbursements", to: "/admin/reimbursements", icon: Receipt, capability: "canPayClaims" },
  { key: "Reports", to: "/admin/reports", icon: PieChart, capability: "canViewReports" },
];

/**
 * The capability each of those addresses needs, so the route guard and the rail read
 * one table. A path missing from here is workspace-gated and nothing else.
 */
export const ROUTE_CAPABILITY: Readonly<Record<string, keyof Capabilities>> = Object.fromEntries(
  CAPABILITY_ITEMS.map((item) => [item.to, item.capability]),
);

/**
 * The rail this person actually gets: their workspace's set, plus anything a
 * capability opens that the set does not already contain.
 *
 * The dedupe is what keeps an admin's rail byte-identical — they hold both
 * capabilities, and both screens are already in their Organisation group.
 */
export function navFor(
  workspace: Workspace,
  capabilities: Capabilities,
): readonly NavGroup[] {
  const base = NAV[workspace];
  const already = new Set(base.flatMap((group) => group.items.map((item) => item.to)));
  const granted = CAPABILITY_ITEMS.filter(
    (item) => capabilities[item.capability] && !already.has(item.to),
  );
  if (granted.length === 0) return base;
  // Ahead of "Me", where the admin rail also puts the organisation-wide screens.
  return [{ group: "Organisation", items: granted }, ...base];
}

/* --------------------------------- styles -------------------------------- */

const itemStyle = (active: boolean) => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  width: "100%",
  textAlign: "left" as const,
  cursor: "pointer",
  fontFamily: "var(--font-body)",
  fontWeight: active ? 700 : 600,
  fontSize: 12,
  padding: "8px 10px",
  marginBottom: 2,
  borderRadius: 8,
  border: "none",
  borderLeft: `2px solid ${active ? "var(--violet)" : "transparent"}`,
  background: active ? "var(--violet-soft)" : "transparent",
  color: active ? "var(--violet)" : "var(--sub)",
  textDecoration: "none",
});

const groupLabelStyle = (first: boolean) => ({
  fontFamily: "var(--font-body)",
  fontSize: 9.5,
  fontWeight: 700,
  color: "var(--muted)",
  textTransform: "uppercase" as const,
  letterSpacing: 0.8,
  padding: first ? "0 12px 7px" : "16px 12px 7px",
});

/* ----------------------------- identity block ----------------------------- */

/**
 * Who you are, and the sign-out that belongs beside it.
 *
 * `SessionContext.signOut()` — which revokes the server session, drops the Firebase
 * credential and empties the query cache — shipped with no caller anywhere in the app.
 * There was no menu, no button and no route, so the only way off a shared machine was
 * to clear cookies. This is the conventional home for it: the user block at the foot of
 * the rail.
 *
 * It is deliberately a plain control rather than a popover menu. The name and the
 * access role stay *static text*, never the label of a button, because that is what
 * `shell.test.tsx`'s persona-switcher guard checks for: it asserts that no control in
 * the rail is labelled with a role name, which is how D11 ("there is no Viewing as
 * switcher, ever") is enforced mechanically. A trigger reading "Priya Nair / Manager"
 * would have to weaken that guard to ship, and the guard is worth more than the
 * disclosure triangle. Sign-out is one click here instead of two anyway.
 *
 * Nothing here can change the workspace; the nav set is still decided by `me.workspace`
 * and `me.capabilities`, and by nothing else.
 */
function IdentityBlock() {
  const me = useMe();
  const { signOut } = useSession();
  const [signingOut, setSigningOut] = useState(false);

  /**
   * No navigation call here on purpose. Clearing the session re-renders `RequireAuth`,
   * which is what routes to `/signin` — and it unmounts this component on the way, so a
   * `navigate()` afterwards would be a state update on a component that no longer
   * exists. `signingOut` is set before the call and never cleared, for the same reason.
   */
  const handleSignOut = () => {
    setSigningOut(true);
    void signOut();
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 8px 8px" }}>
        <Avatar name={me.employee.name} photoUrl={me.employee.photoUrl} size={28} />
        <div style={{ minWidth: 0 }}>
          <div className="bp-identity__name">{me.employee.name}</div>
          <div className="bp-identity__role">{me.accessRole}</div>
        </div>
      </div>
      <button
        type="button"
        className="bp-identity__signout"
        onClick={handleSignOut}
        disabled={signingOut}
      >
        <LogOut size={14} aria-hidden="true" />
        {signingOut ? "Signing out…" : "Sign out"}
      </button>
    </div>
  );
}

/* -------------------------------- component ------------------------------- */

export interface SidebarProps {
  alertCount: number;
  alertsOpen: boolean;
  onToggleAlerts: () => void;
  /**
   * Whether the off-canvas rail is showing. Below `--bp-md` the rail is a drawer; at
   * full width the class does nothing and the rail is always visible.
   */
  open?: boolean;
  /** Called when a nav item is chosen, so the shell can close that drawer again. */
  onNavigate?: () => void;
}

export function Sidebar({
  alertCount,
  alertsOpen,
  onToggleAlerts,
  open = false,
  onNavigate,
}: SidebarProps) {
  const me = useMe();
  const { theme, setTheme } = useTheme();
  const location = useLocation();

  // The workspace's set plus whatever a capability opens on top of it — so the rail
  // offers exactly what the route guard will let this person through to.
  const groups = navFor(me.workspace, me.capabilities);
  const settingsActive = location.pathname.startsWith("/admin/settings");

  return (
    <aside
      id="app-sidebar"
      // Layout lives in `styles/shell.css` — inline styles cannot carry a media query,
      // which is why the ported app had no responsive behaviour at all.
      className={["bp-sidebar", open ? "bp-sidebar--open" : null].filter(Boolean).join(" ")}
      style={{
        background: "var(--surface)",
        borderRight: "1px solid var(--line)",
        padding: "20px 12px 14px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Brand */}
      <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "0 8px 4px" }}>
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: "var(--violet)",
            display: "grid",
            placeItems: "center",
            fontFamily: "var(--font-head)",
            fontWeight: 800,
            color: "#fff",
            fontSize: 15,
          }}
          aria-hidden="true"
        >
          B
        </div>
        <div>
          <div
            style={{
              fontFamily: "var(--font-head)",
              fontWeight: 800,
              fontSize: 16,
              color: "var(--ink)",
              letterSpacing: -0.3,
            }}
          >
            Botpresso<span style={{ color: "var(--violet)" }}>HR</span>
          </div>
          <div
            style={{
              fontFamily: "var(--font-body)",
              fontSize: 10,
              color: "var(--muted)",
              fontWeight: 600,
            }}
          >
            People operations
          </div>
        </div>
      </div>

      {/* Navigation — the set is fixed by me.workspace and me.capabilities, both of
          which come off the wire, and by nothing else. */}
      <nav style={{ flex: 1, marginTop: 8 }} aria-label="Main">
        {groups.map((group, groupIndex) => (
          <div key={group.group}>
            <div style={groupLabelStyle(groupIndex === 0)}>{group.group}</div>
            {group.items.map((item) => (
              <NavLink
                key={`${group.group}:${item.key}`}
                to={item.to}
                // NavLink matches on prefix, so `/team` also lit up while you were
                // on `/team/reimbursements` and two rail items read as current at
                // once. No nav target has nested detail routes, so every one is a
                // leaf and an exact match is the honest test.
                end
                onClick={onNavigate}
                style={({ isActive }) => itemStyle(isActive)}
              >
                {({ isActive }) => (
                  <>
                    <item.icon size={15} aria-hidden="true" />
                    <span>{item.key}</span>
                    {isActive ? <span className="bp-sr-only">(current page)</span> : null}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* Command Center */}
      <div style={{ borderTop: "1px solid var(--line)", marginTop: 14, paddingTop: 12 }}>
        <div
          style={{
            fontFamily: "var(--font-body)",
            fontSize: 9.5,
            fontWeight: 700,
            color: "var(--amber)",
            textTransform: "uppercase",
            letterSpacing: 1,
            padding: "0 12px 7px",
          }}
        >
          Command Center
        </div>
        <button
          type="button"
          onClick={onToggleAlerts}
          aria-expanded={alertsOpen}
          style={{ ...itemStyle(alertsOpen), fontWeight: 600 }}
        >
          <BellRing size={15} aria-hidden="true" />
          <span style={{ flex: 1 }}>Alerts</span>
          {/* No pill at zero. A red badge reading "0" reports calm as an
              emergency — and now that the server only emits actionable rows,
              zero is a state the rail actually reaches. */}
          {alertCount > 0 ? (
            <span
              style={{
                minWidth: 17,
                height: 15,
                padding: "0 4px",
                borderRadius: 999,
                background: "var(--red)",
                color: "#fff",
                fontFamily: "var(--font-mono)",
                fontWeight: 700,
                fontSize: 9,
                display: "grid",
                placeItems: "center",
              }}
              aria-label={`${alertCount} alerts`}
            >
              {alertCount}
            </span>
          ) : null}
        </button>
        {me.capabilities.canManageSettings ? (
          <NavLink to="/admin/settings" onClick={onNavigate} style={itemStyle(settingsActive)}>
            <SettingsIcon size={15} aria-hidden="true" />
            <span>Settings</span>
          </NavLink>
        ) : null}
      </div>

      {/* Light / Dark */}
      <div style={{ padding: "12px 8px 0" }}>
        <div
          role="group"
          aria-label="Theme"
          style={{
            display: "flex",
            background: "var(--bg)",
            border: "1px solid var(--line)",
            borderRadius: 9,
            padding: 3,
          }}
        >
          {([["light", Sun], ["dark", Moon]] as const).map(([value, Icon]) => (
            <button
              key={value}
              type="button"
              onClick={() => setTheme(value as Theme)}
              aria-pressed={theme === value}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                cursor: "pointer",
                fontFamily: "var(--font-body)",
                fontWeight: 700,
                fontSize: 10.5,
                padding: "6px 0",
                borderRadius: 7,
                border: "none",
                background: theme === value ? "var(--surface)" : "transparent",
                color: theme === value ? "var(--violet)" : "var(--muted)",
                boxShadow: theme === value ? "0 1px 3px rgba(0,0,0,.15)" : "none",
                textTransform: "capitalize",
              }}
            >
              <Icon size={12} aria-hidden="true" /> {value}
            </button>
          ))}
        </div>
      </div>

      {/* Who you are, and how to stop being them. Nothing here changes the workspace. */}
      <div style={{ borderTop: "1px solid var(--line)", marginTop: 12, paddingTop: 10 }}>
        <IdentityBlock />
        <div
          style={{
            fontFamily: "var(--font-body)",
            fontSize: 9.5,
            color: "var(--muted)",
            lineHeight: 1.6,
            padding: "8px 8px 0",
          }}
        >
          {COMPANY.legal}
          <br />
          Leave year 1 Jan – 31 Dec 2026
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
